import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { isMobile } from '@databases/modes';
import React, { type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { messages } from './messages';

const searchIconWrapperStyles = xcss({
  paddingInlineStart: 'space.100',
});

const searchIcon = (
  <Flex xcss={searchIconWrapperStyles}>
    <SearchIcon label="" spacing="spacious" />
  </Flex>
);

const clearActionStyles = xcss({
  marginRight: 'space.075',
  cursor: 'pointer',
});

export interface SearchBarProps {
  searchFieldRef?: React.RefObject<HTMLInputElement>;
  value: string | undefined;
  handleOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnClear: () => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
  cancelButtonAlwaysOn?: boolean;
}

// TODO: Use compiled css instead of styled-components
// https://atlassian.design/components/textfield/examples#customization
const MobileTextfield = styled(Textfield)`
  [data-ds--text-field--input] {
    font: ${token('font.body.large', '16px')};
  }
`;

export const SearchBar: FunctionComponent<SearchBarProps> = ({
  searchFieldRef,
  value = '',
  handleOnChange,
  handleOnClear,
  placeholder = '',
  autoComplete,
  autoFocus,
  onBlur,
  cancelButtonAlwaysOn = false,
}) => {
  const { formatMessage } = useIntl();

  const TextfieldComponent = isMobile() ? MobileTextfield : Textfield;

  const clearSearchButton = () => {
    return (
      <Box
        role="button"
        tabIndex={0}
        xcss={clearActionStyles}
        onClick={handleOnClear}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOnClear();
          }
        }}
        aria-label={formatMessage(messages['search-bar.clear-label'])}
      >
        <CrossCircleIcon spacing="spacious" label="" />
      </Box>
    );
  };

  return (
    <TextfieldComponent
      ref={searchFieldRef}
      value={value}
      onChange={handleOnChange}
      placeholder={placeholder}
      aria-label={placeholder}
      elemBeforeInput={searchIcon}
      elemAfterInput={(value.length > 0 || cancelButtonAlwaysOn) && clearSearchButton()}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      onBlur={onBlur}
      testId="search-bar"
    />
  );
};
